<template>
  <Modal :modal-active="modelValue" max-width="max-w-3xl" @close="handleClose">
    <template #header>
      <h2 class="text-lg font-bold text-secondary50 text-left">Detalhes da validação</h2>
    </template>

    <template v-if="allValidated">
      <div class="flex flex-col items-center justify-center py-16">
        <div class="w-24 h-24 rounded-full bg-[#eafbef] flex items-center justify-center mb-6">
          <v-icon size="56" color="#198038">{{ mdiCheckCircle }}</v-icon>
        </div>
        <h3 class="text-lg font-semibold text-[#16161d]">Nenhum médico pendente de validação</h3>
      </div>
    </template>

    <template v-else-if="currentDoctor">
      <div class="grid grid-cols-2 gap-6">
        <BiometryEvidencePanel :biometry-id="currentDoctor.id" max-height-class="max-h-[500px]" />

        <div class="flex flex-col gap-5">
          <DoctorInfoCard :doctor="currentDoctor" />

          <div class="text-left">
            <p class="text-base font-semibold text-[#16161d] mb-2">Validação</p>
            <div class="flex flex-col gap-2">
              <label class="flex items-center gap-2 cursor-pointer" data-testid="approve-radio">
                <input
                  v-model="validationChoice"
                  type="radio"
                  value="VERIFIED"
                  data-testid="approve-radio-input"
                  class="w-5 h-5 accent-[#2f7f91]"
                />
                <span class="text-base text-[#16161d]">Aprovar</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer" data-testid="reject-radio">
                <input
                  v-model="validationChoice"
                  type="radio"
                  value="REJECTED"
                  data-testid="reject-radio-input"
                  class="w-5 h-5 accent-[#2f7f91]"
                />
                <span class="text-base text-[#16161d]">Reprovar</span>
              </label>
            </div>
          </div>

          <template v-if="validationChoice === 'REJECTED'">
            <div class="flex flex-col gap-1.5">
              <v-select
                v-model="selectedReason"
                :items="rejectionOptions"
                data-testid="rejection-reason-select"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
              >
                <template #label>
                  Motivo da reprovação
                  <span class="text-[#da1e28]">*</span>
                </template>
              </v-select>
              <p class="text-xs text-[#7f7f8e] text-left">O motivo é registrado no histórico</p>
            </div>
            <v-textarea
              v-if="selectedReason === 'OTHER'"
              v-model="customReason"
              data-testid="rejection-reason-textarea"
              variant="outlined"
              density="comfortable"
              placeholder="Digite o motivo da reprovação..."
              hide-details="auto"
              rows="2"
            />
          </template>

          <BiometryHistoryCard :doctor="currentDoctor" />
        </div>
      </div>
    </template>

    <template #footer>
      <div v-if="allValidated" class="w-full">
        <FilledButton
          data-testid="validation-close-final-button"
          text="Fechar"
          width="100%"
          height="48px"
          @click="handleClose"
        />
      </div>
      <div v-else class="flex gap-4 w-full">
        <div class="flex-1">
          <OutlineButton
            data-testid="validation-cancel-button"
            text="Fechar"
            width="100%"
            height="48px"
            @click="handleClose"
          />
        </div>
        <div class="flex-1">
          <FilledButton
            data-testid="validation-next-button"
            :text="isLastStep ? 'Finalizar' : 'Próxima validação'"
            width="100%"
            height="48px"
            :disabled="!canSubmit || isProcessing"
            :prepend-icon="mdiArrowRight"
            @click="handleNextValidation"
          />
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { mdiCheckCircle, mdiArrowRight } from '@mdi/js'
import Modal from '@/components/designSystem/modals/Modal.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import BiometryEvidencePanel from './BiometryEvidencePanel.vue'
import BiometryHistoryCard from './BiometryHistoryCard.vue'
import DoctorInfoCard from './DoctorInfoCard.vue'
import { useFacialBiometryStore } from '@/pinia-store/useFacialBiometryStore'
import { REJECTION_REASON_OPTIONS } from '@/constants/facial-biometry.constants'
import type { ValidateFacialBiometryPayload } from '@/types/facial-biometry.types'

defineProps<{
  modelValue: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  validated: []
}>()

const store = useFacialBiometryStore()
const { currentDoctor, isLastStep, allValidated, isProcessing } = storeToRefs(store)

const validationChoice = ref<'VERIFIED' | 'REJECTED' | ''>('')
const selectedReason = ref('')
const customReason = ref('')
const rejectionOptions = REJECTION_REASON_OPTIONS

const canSubmit = computed(() => {
  if (!validationChoice.value) return false
  if (validationChoice.value === 'REJECTED') {
    if (!selectedReason.value) return false
    if (selectedReason.value === 'OTHER' && !customReason.value.trim()) return false
  }
  return true
})

watch(currentDoctor, () => {
  validationChoice.value = ''
  selectedReason.value = ''
  customReason.value = ''
})

async function handleNextValidation() {
  if (!currentDoctor.value?.id || !validationChoice.value) return

  const payload: ValidateFacialBiometryPayload = {
    status: validationChoice.value as 'VERIFIED' | 'REJECTED'
  }

  if (validationChoice.value === 'REJECTED') {
    payload.rejectionReason =
      selectedReason.value === 'OTHER' ? customReason.value.trim() : selectedReason.value
  }

  const success = await store.validateBiometry(currentDoctor.value.id, payload)
  if (success) {
    store.nextStep()
    emit('validated')
  }
}

function handleClose() {
  emit('update:modelValue', false)
}
</script>
